import type { CrewlinkClient } from '../supabase-client';
import type { CaptureRow, CaptureStatus } from '../database.types';
import { isStaleStatusWrite } from './update-project-status';

/**
 * Writer wrapper for `update_capture_status` (migration 0008). M6 / S06-02.
 *
 * The layering rule (CLAUDE.md §8, INV-T02): UI → query hooks → writer
 * wrappers → Postgres RPC. The screen never calls `.rpc(...)` and never calls
 * `.update(...)` on `capture` at all — no such grant exists anyway.
 *
 * Ownership is not a parameter; it comes from `auth.uid()` inside the function
 * (INV-T23). `expectedStatus` is the concurrency guard: the change only lands
 * while the Capture is still in that status.
 *
 * **CHANGING A CAPTURE'S STATUS NEVER TOUCHES ITS PROJECT.** There is no
 * Project argument here and no Project write inside the function — which is
 * how that guarantee is kept, rather than by remembering not to.
 */
export async function updateCaptureStatus(
  client: CrewlinkClient,
  captureId: string,
  expectedStatus: CaptureStatus,
  newStatus: CaptureStatus,
): Promise<CaptureRow> {
  const { data, error } = await client.rpc('update_capture_status', {
    p_capture_id: captureId,
    p_expected_status: expectedStatus,
    p_new_status: newStatus,
  });

  if (error) throw error;
  if (!data) throw new Error('update_capture_status returned no capture row');

  return data;
}

/**
 * Turn a failure into something Brandon can act on.
 *
 * **The stale case gets its own sentence, and it is the one that matters.**
 * "Try again" would be wrong advice: the screen he is looking at is out of
 * date, and the honest thing is to say the Capture changed elsewhere and show
 * him what it is now. The wording deliberately mirrors the Project one so the
 * two read as the same product speaking.
 *
 * The stale test itself is `isStaleStatusWrite`, reused from the Project
 * writer rather than re-implemented — one definition of "this was the
 * concurrency guard", so the two cannot drift apart. (I-006 already records
 * that it matches on message text rather than a structured code; that is
 * unchanged here and is not made worse by reuse.)
 */
export function describeCaptureStatusChangeError(message: string, code?: string): string {
  const normalised = message.toLowerCase();

  if (isStaleStatusWrite({ code, message })) {
    return 'This Capture was changed somewhere else. Showing its current status.';
  }
  if (normalised.includes('not found') || normalised.includes('p0002')) {
    return 'That Capture is no longer available.';
  }
  if (normalised.includes('not authenticated') || normalised.includes('42501')) {
    return 'Your session has expired. Sign in again and try once more.';
  }
  if (normalised.includes('invalid input value for enum')) {
    return 'That is not a valid status.';
  }
  if (normalised.includes('failed to fetch') || normalised.includes('network')) {
    return 'Could not reach Crewlink. Check your connection and try again.';
  }
  return 'Could not change the status. Please try again.';
}
